import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileText, PlayCircle, AlertTriangle } from 'lucide-react';
import TrainingShell from '../../components/capacitacion/TrainingShell';
import VideoPlayer from '../../components/capacitacion/VideoPlayer';
import { trainingApi } from '../../services/trainingApi';
import { useTrainingUser } from '../../hooks/useTrainingUser';

export default function TrainingModuloDetalle() {
  const { slug } = useParams<{ slug: string }>();
  const { user, isLoading: userLoading } = useTrainingUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userLoading && !user) navigate('/capacitacion', { replace: true });
  }, [userLoading, user, navigate]);

  const { data } = useQuery({
    queryKey: ['training-progress', user?.id],
    queryFn: () => trainingApi.getProgress(user!.id),
    enabled: !!user?.id,
  });

  const item = useMemo(
    () => data?.progreso.find((p) => p.modulo.slug === slug),
    [data, slug]
  );

  const startMutation = useMutation({
    mutationFn: () =>
      trainingApi.startAttempt({ usuarioId: user!.id, moduloId: item!.modulo.id }),
    onSuccess: (res) => {
      sessionStorage.setItem(
        `training-start-${res.attemptId}`,
        JSON.stringify(res)
      );
      navigate(`/capacitacion/prueba/${res.attemptId}`);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Error al iniciar';
      const after = err?.response?.data?.reintentar_despues_de;
      if (after) {
        setError(`${msg}. Disponible a las: ${new Date(after).toLocaleString()}`);
      } else {
        setError(msg);
      }
    },
  });

  if (!user) return null;
  if (!item) {
    return (
      <TrainingShell>
        <p className="text-gray-400">Cargando módulo...</p>
      </TrainingShell>
    );
  }

  const yaAprobado = item.status === 'aprobado';
  const bloqueado = !!item.reintentar_despues_de && new Date(item.reintentar_despues_de) > new Date();

  return (
    <TrainingShell>
      <button
        onClick={() => navigate('/capacitacion/modulos')}
        className="text-sm text-ngs-cyan hover:underline mb-4"
      >
        ← Volver a módulos
      </button>

      <h1 className="text-2xl font-bold mb-1">{item.modulo.nombre}</h1>
      <p className="text-gray-400 mb-6">{item.modulo.descripcion}</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <VideoPlayer url={item.modulo.video_url} />
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-ngs-cyan/20 bg-ngs-surface p-5">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3">
              Material
            </h3>
            {item.modulo.manual_url ? (
              <a
                href={item.modulo.manual_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-ngs-cyan hover:underline"
              >
                <FileText className="h-4 w-4" />
                Descargar manual PDF
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 text-gray-500">
                <FileText className="h-4 w-4" />
                Manual pendiente
              </span>
            )}
          </div>

          <div className="rounded-xl border border-ngs-cyan/20 bg-ngs-surface p-5">
            <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3">
              Prueba
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              5 preguntas aleatorias. Umbral: <b className="text-white">{item.modulo.umbral_aprobacion}%</b>.
              Reintento: {item.modulo.tiempo_reintento_horas}h.
            </p>

            {yaAprobado && (
              <div className="rounded-md border border-green-500/40 bg-green-500/10 text-green-300 text-sm p-3 mb-3">
                Ya aprobaste este módulo.
              </div>
            )}

            {bloqueado && item.reintentar_despues_de && (
              <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 text-sm p-3 mb-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Reintento disponible a las{' '}
                  {new Date(item.reintentar_despues_de).toLocaleString()}
                </span>
              </div>
            )}

            {error && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3 mb-3">
                {error}
              </div>
            )}

            <button
              onClick={() => {
                setError(null);
                startMutation.mutate();
              }}
              disabled={yaAprobado || bloqueado || startMutation.isLoading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-ngs-orange text-ngs-bg font-semibold py-2.5 hover:bg-ngs-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayCircle className="h-4 w-4" />
              {startMutation.isLoading ? 'Iniciando...' : 'Iniciar prueba'}
            </button>
          </div>
        </aside>
      </div>
    </TrainingShell>
  );
}
